import { FC } from 'react'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { useTranslation } from 'next-i18next'
import { observer } from 'mobx-react'
import { KeywordSearchGroupFormForAngels } from '@components-v2/molecules/KeywordSearchGroupFormForAngels'
import { CurrencyUnit, ILocationBase, IMarketBase, OrderDirection, UserOrderBase, UserOrderField } from '@/types'
import { InvestmentTargetAmountSelectField } from '@components-v2/molecules/InvestmentTargetAmountSelectField'
import { LocationSearchSelectField } from '@components-v2/molecules/LocationSearchSelectField'
import { RoundsMultipleSelectField } from '@components-v2/molecules/RoundsMultipleSelectField'
import { MarketSearchSelectField } from '@components-v2/molecules/MarketSearchSelectField'
import styles from './index.module.scss'

type Value = {
  keyword: string
  locations: ILocationBase[]
  investmentAmount: {
    maxAmount?: string
    minAmount?: string
  }
  unit: CurrencyUnit
  roundIds: string[]
  markets: IMarketBase[]
  orderBy: UserOrderBase
}

type Props = {
  search: (
    keyword: string,
    locations: string[],
    maxAmount: string,
    minAmount: string,
    unit: CurrencyUnit,
    roundIds: string[],
    markets: string[],
    orderBy: UserOrderBase
  ) => Promise<void>
}

export const KeywordSearchGroupForAngels: FC<Props> = observer(({ search }) => {
  const { t } = useTranslation(['angels-vcs-startups', 'search'])
  const initialValue: Value = {
    keyword: '',
    locations: [],
    investmentAmount: undefined,
    unit: CurrencyUnit.USD,
    roundIds: [],
    markets: [],
    orderBy: {
      direction: OrderDirection.DESC,
      userOrderField: UserOrderField.ANGEL_SCORE,
    },
  }
  const validationSchema = Yup.object().shape({})

  const setSearch = async (values: Value) => {
    await search(
      values.keyword,
      values.locations.map((location) => location.id),
      values.investmentAmount?.maxAmount,
      values.investmentAmount?.minAmount,
      values.unit,
      values.roundIds,
      values.markets.map((market) => market.id),
      values.orderBy
    )
  }

  return (
    <Formik
      initialValues={initialValue}
      validationSchema={validationSchema}
      onSubmit={(values: Value, actions) => {
        setSearch(values)
        actions.setSubmitting(false)
      }}
    >
      {({ setFieldValue, values, submitForm, isSubmitting }) => {
        const setUserOrderField = (v: UserOrderField) => {
          setFieldValue('orderBy.userOrderField', v)
          submitForm()
        }
        const setLocations = (v: ILocationBase[]) => {
          setFieldValue('locations', v)
          submitForm()
        }
        const setInvestmentAmount = (v: { maxAmount?: string; minAmount?: string }) => {
          setFieldValue('investmentAmount', v)
          submitForm()
        }
        const setRoundIds = (v: string[]) => {
          setFieldValue('roundIds', v)
          submitForm()
        }
        const setMarkets = (v: IMarketBase[]) => {
          setFieldValue('markets', v)
          submitForm()
        }

        return (
          <KeywordSearchGroupFormForAngels
            selectedUserOrder={values.orderBy}
            setUserOrderField={setUserOrderField}
            isSubmitting={isSubmitting}
          >
            <div className={styles.container}>
              <LocationSearchSelectField
                placeholder={t('所在地')}
                selectedLocations={values.locations}
                setFieldValue={setLocations}
              />
              <InvestmentTargetAmountSelectField
                placeholder={t('平均投資金額')}
                selectedValue={values.investmentAmount}
                setFieldValue={setInvestmentAmount}
              />
              <RoundsMultipleSelectField
                placeholder={t('投資対象ラウンド')}
                selectedRoundIds={values.roundIds}
                setFieldValue={setRoundIds}
              />
              <MarketSearchSelectField
                placeholder={t('投資対象領域')}
                selectedMarkets={values.markets}
                setFieldValue={setMarkets}
              />
            </div>
          </KeywordSearchGroupFormForAngels>
        )
      }}
    </Formik>
  )
})
